/**
 * Общие компоненты для Module Federation
 * Переиспользуемые обертки для ошибок, загрузки и безопасного рендеринга удаленных модулей
 */

import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Spin } from 'antd';

/**
 * HOC для добавления переводов к class components
 */
const withTranslation = Component => {
  return React.forwardRef((props, ref) => {
    const { t } = useTranslation('remoteModule');
    return <Component {...props} t={t} ref={ref} />;
  });
};

/**
 * Error Boundary для удаленных модулей с улучшенным UX
 */
class RemoteModuleErrorBoundaryComponent extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('🚨 Remote module error:', error.message);
    this.setState({ errorInfo });

    // Передаем ошибку родительскому компоненту если есть onError callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Отправляем в error tracking service в production
    if (typeof window !== 'undefined' && window.reportError && process.env.NODE_ENV === 'production') {
      window.reportError(error);
    }
  }

  render() {
    const { t } = this.props;

    if (this.state.hasError) {
      // Если передан кастомный fallback, используем его
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.state.errorInfo);
      }

      // Дефолтный fallback UI
      return (
        <div
          style={{
            padding: '24px',
            border: '1px solid #ff7875',
            borderRadius: '8px',
            margin: '20px',
            backgroundColor: '#fff1f0',
            boxShadow: '0 2px 8px rgba(255, 77, 79, 0.1)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
            <span style={{ fontSize: '20px', marginRight: '8px' }}>🚨</span>
            <h3 style={{ margin: 0, color: '#ff4d4f' }}>{t('errorBoundary.title')}</h3>
          </div>

          <p style={{ color: '#8c8c8c', marginBottom: '16px' }}>{t('errorBoundary.description')}</p>

          {this.props.showDetails !== false && (
            <details style={{ marginBottom: '16px' }}>
              <summary style={{ cursor: 'pointer', color: '#1890ff', marginBottom: '8px' }}>
                📋 {t('errorBoundary.errorDetails')}
              </summary>
              <div
                style={{
                  background: '#f5f5f5',
                  padding: '12px',
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  color: '#d73027',
                  whiteSpace: 'pre-wrap',
                  overflowX: 'auto',
                }}
              >
                <div>
                  <strong>{t('errorBoundary.message')}</strong>{' '}
                  {this.state.error?.message || t('errorBoundary.unknownError')}
                </div>
                {this.state.errorInfo?.componentStack && (
                  <div style={{ marginTop: '8px' }}>
                    <strong>{t('errorBoundary.componentStack')}</strong>
                    <br />
                    {this.state.errorInfo.componentStack}
                  </div>
                )}
              </div>
            </details>
          )}

          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <Button type='primary' onClick={() => window.location.reload()} icon='🔄'>
              {t('errorBoundary.reloadPage')}
            </Button>

            <Button onClick={() => window.history.back()} icon='←'>
              {t('errorBoundary.goBack')}
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Экспортируем ErrorBoundary с поддержкой переводов
export const RemoteModuleErrorBoundary = withTranslation(RemoteModuleErrorBoundaryComponent);

/**
 * Компонент ошибки загрузки удаленного модуля
 */
export const RemoteModuleLoadError = ({ error, onRetry, moduleName = 'remote module' }) => {
  const { t } = useTranslation('remoteModule');

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  return (
    <div
      style={{
        padding: '24px',
        border: '1px solid #ff4d4f',
        borderRadius: '8px',
        margin: '20px',
        backgroundColor: '#fff2f0',
        boxShadow: '0 2px 8px rgba(255, 77, 79, 0.1)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <span style={{ fontSize: '20px', marginRight: '8px' }}>⚠️</span>
        <h3 style={{ margin: 0, color: '#ff4d4f' }}>{t('loadError.title')}</h3>
      </div>

      <p style={{ color: '#8c8c8c', marginBottom: '16px' }}>{t('loadError.description', { moduleName })}</p>

      <details style={{ marginBottom: '16px' }}>
        <summary style={{ cursor: 'pointer', color: '#1890ff', marginBottom: '8px' }}>
          📋 {t('loadError.errorDetails')}
        </summary>
        <div
          style={{
            background: '#f5f5f5',
            padding: '12px',
            borderRadius: '4px',
            fontFamily: 'monospace',
            fontSize: '12px',
            color: '#d73027',
            whiteSpace: 'pre-wrap',
            overflowX: 'auto',
          }}
        >
          <div>
            <strong>{t('loadError.message')}</strong> {error?.message || t('loadError.unknownError')}
          </div>
          {error?.stack && (
            <div style={{ marginTop: '8px' }}>
              <strong>{t('loadError.stack')}</strong>
              <br />
              {error.stack}
            </div>
          )}
        </div>
      </details>

      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        <Button type='primary' onClick={handleRetry} icon='🔄'>
          {t('loadError.retry')}
        </Button>

        <Button onClick={() => window.history.back()} icon='←'>
          {t('loadError.goBack')}
        </Button>
      </div>
    </div>
  );
};

/**
 * Компонент загрузки для удаленных модулей
 */
export const RemoteModuleLoading = ({ moduleName = 'remote module', size = 'large', minHeight = '400px' }) => {
  const { t } = useTranslation('remoteModule');

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight,
        padding: '40px',
        backgroundColor: '#fafafa',
        borderRadius: '8px',
        margin: '20px',
      }}
    >
      <Spin size={size} style={{ marginBottom: '20px' }} />
      <div style={{ textAlign: 'center' }}>
        <h3 style={{ color: '#1890ff', marginBottom: '8px' }}>{t('loading.title')}</h3>
        <p style={{ color: '#8c8c8c', margin: 0 }}>{t('loading.description', { moduleName })}</p>
      </div>
    </div>
  );
};
